import React, { useEffect, useMemo, useState } from "react";
import { tableUseStyle } from "../../etc/tableusestyle";
import { DataGridPremium, GridRowModel } from "@mui/x-data-grid-premium";
import { Button, Input, Modal, ModalDialog, Radio, RadioGroup, Tooltip } from "@mui/joy";
import { Image } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import ImageModal2 from "../../../use/imagemodal2";
import GiftCashCard from "./giftcash/giftcashcard";
import { GIFT_CASH_BRANCHES, giftCashBranchLabel, toastOptions } from "./giftcash/giftcashutil";

//등록인증 이벤트(네이버 리뷰) 제출자 관리 - 본사직원페이지 탭
//선물종류 값 -> 한글 라벨
const giftNames: { [key: string]: string } = {
    medical: "메디컬북",
    starbucks: "스타벅스 아메리카노 T 1잔"
};

const NaverReviewWriter: React.FC<any> = (props) => {

    const classes = tableUseStyle();

    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [imageOpen, setImageOpen] = useState<boolean>(false);
    const [imageSrc, setImageSrc] = useState<string>("");
    //기프티콘 캐쉬 카드 다시 조회 트리거
    const [cashRefreshKey, setCashRefreshKey] = useState<number>(0);
    //지급하기 — 매칭 0명/여러 명이라 차감 지점을 고르는 중인 행
    const [branchPickRow, setBranchPickRow] = useState<any>(null);
    const [pickedBranch, setPickedBranch] = useState<string>("");
    //발송 중인 행 id (연타 방지)
    const [sendingId, setSendingId] = useState<number | null>(null);

    const fetchList = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch("/dashboard/reviewEvent/list");
            if (result.message === "success" && Array.isArray(result.data)) {
                setRows(result.data);
            } else {
                console.log(result);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    //선물 지급 처리 / 취소
    const handleGift = async (id: number, done: boolean) => {
        if (!window.confirm(done ? "선물 지급 완료로 처리할까요?" : "지급 완료 처리를 취소할까요?")) return;
        try {
            const result: any = await postFetch("/dashboard/reviewEvent/gift", { id, done });
            if (result.message === "success") {
                fetchList();
            } else {
                alert(`처리 실패: ${result.message}`);
            }
        } catch (e) {
            console.log(e);
            alert("처리 중 오류가 발생했습니다.");
        }
    };

    //스타벅스 지급하기 — 매칭 지점이 정해져 있으면 바로, 아니면 지점 선택 모달
    const handleSendClick = (row: any) => {
        if (row.giftCashBranch === "choose") {
            setPickedBranch("");
            setBranchPickRow(row);
            return;
        }
        sendGift(row, row.giftCashBranch);
    };

    //카카오톡 선물하기로 실제 발송 + 지점 캐쉬 차감 (POST /dashboard/reviewEvent/sendGift)
    const sendGift = async (row: any, branchKey: string) => {
        if (!branchKey || sendingId) return;
        if (!window.confirm(`${row.name}(${row.phone})에게 스타벅스 아메리카노 T를 카카오톡으로 보냅니다.\n${giftCashBranchLabel(branchKey)} 캐쉬에서 차감돼요. 보낼까요?`)) return;
        try {
            setSendingId(row.id);
            const result: any = await postFetch("/dashboard/reviewEvent/sendGift", { id: row.id, branchKey });
            if (result.message === "success") {
                toast.success(`${row.name}님께 선물을 보냈어요`, toastOptions);
                if (!result.deducted) {
                    toast.warn("발송은 됐지만 캐쉬 차감에 실패했어요. 서버 로그를 확인해 주세요", { ...toastOptions, autoClose: 8000 });
                }
                setBranchPickRow(null);
            } else {
                console.log(result);
                toast.error(`발송 실패: ${result.message}`, toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("발송 중 오류가 발생했습니다", toastOptions);
        } finally {
            setSendingId(null);
            fetchList();
            setCashRefreshKey((k) => k + 1);
        }
    };

    //메모 인라인 편집 저장 (memo 필드만 허용)
    const handleEditCommit = React.useCallback(async (newRow: GridRowModel, old: any) => {

        var field = "";

        for (const [key, value] of Object.entries(newRow)) {
            if (value !== old[key]) {
                field = key;
            }
        }

        if (!field) {
            return old;
        }

        if (field !== "memo") {
            console.log("not editable field edited");
            return old;
        }

        try {
            const result: any = await postFetch("/dashboard/reviewEvent/memo", {
                id: newRow.id,
                memo: newRow.memo ? newRow.memo : ""
            });

            if (result.message !== "success") {
                alert("메모 저장에 실패했어요.");
                return old;
            }
        } catch (e) {
            console.log(e);
            alert("메모 저장 중 오류가 발생했습니다.");
            return old;
        }

        return newRow;
    }, []);

    const handleProcessRowUpdateError = React.useCallback((error: Error) => {
        console.log(error);
    }, []);

    //검색 : 매칭된 학생 이름(제출 이름 포함) 또는 제출 전화번호
    const filteredRows = useMemo(() => {
        const query = searchText.trim();
        if (!query) return rows;

        const digits = query.replace(/[^0-9]/g, "");

        return rows.filter((row: any) => {
            if (row.matchedName && String(row.matchedName).includes(query)) return true;
            if (row.name && String(row.name).includes(query)) return true;
            if (digits.length >= 2) {
                const rowPhone = String(row.phone || "").replace(/[^0-9]/g, "");
                if (rowPhone.includes(digits)) return true;
            }
            return false;
        });
    }, [rows, searchText]);

    const columns: any[] = [
        {
            field: "createdAt",
            headerName: "제출일시",
            width: 150,
            renderCell: (params: any) => {
                if (!params.value) return "";
                const d = new Date(params.value);
                return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
            }
        },
        { field: "name", headerName: "이름", width: 90 },
        { field: "phone", headerName: "전화번호", width: 130 },
        { field: "nickname", headerName: "닉네임", width: 140 },
        {
            field: "gift",
            headerName: "선물종류",
            width: 170,
            renderCell: (params: any) => {
                if (!params.value) return "";
                return giftNames[params.value] ? giftNames[params.value] : params.value;
            }
        },
        {
            field: "imageSrc",
            headerName: "이미지",
            width: 70,
            sortable: false,
            renderCell: (params: any) => {
                if (params.value) {
                    return (
                        <div
                            onClick={() => { setImageSrc(params.value); setImageOpen(true); }}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", cursor: "pointer" }}
                        >
                            <Image color="success" />
                        </div>
                    );
                }
                return (<div>-</div>);
            }
        },
        { field: "matchedName", headerName: "매칭된 학생이름", width: 120 },
        { field: "matchedLocation", headerName: "매칭된 학생지점", width: 110 },
        { field: "matchedSeat", headerName: "매칭된 학생자리", width: 110 },
        {
            field: "duplicateCount",
            headerName: "중복 횟수",
            width: 90,
            renderCell: (params: any) => {
                if (params.value === null || params.value === undefined) return "";
                if (+params.value >= 2) {
                    return <span style={{ color: "#d32f2f", fontWeight: 700 }}>{params.value}회</span>;
                }
                return <span>{params.value}회</span>;
            }
        },
        {
            field: "giftDone",
            headerName: "선물 지급 여부",
            width: 220,
            renderCell: (params: any) => {
                if (params.value) {
                    //"userId__이름__timestamp" 형식
                    const name = params.value.split("__")[1];
                    const time = +params.value.split("__")[2];
                    const date = new Date(time);
                    const dateString = `${date.getMonth() + 1}/${date.getDate()}일 ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
                    //지급하기로 실제 카톡 발송된 건은 취소 불가 (선물이 회수되지 않음)
                    const isSending = params.value.endsWith("__sending");
                    return (
                        <span>
                            {name} {dateString} {isSending ? "발송 중" : ""} &nbsp;
                            {!params.row.autoSent && !isSending &&
                                <Button variant="soft" size="sm" color="neutral" onClick={() => handleGift(params.row.id, false)}>
                                    취소하기
                                </Button>
                            }
                        </span>
                    );
                }
                //스타벅스는 지급하기 = 카카오톡 선물하기 실제 발송
                if (params.row.gift === "starbucks") {
                    const unsupported = params.row.giftCashBranch === null;
                    const button = (
                        <Button
                            variant="solid"
                            size="sm"
                            color="primary"
                            disabled={unsupported || !!sendingId}
                            loading={sendingId === params.row.id}
                            onClick={() => handleSendClick(params.row)}
                        >
                            지급하기
                        </Button>
                    );
                    if (unsupported) {
                        return (
                            <Tooltip title="캐쉬 관리 지점이 아니라 보낼 수 없어요" variant="soft" size="sm">
                                <span>{button}</span>
                            </Tooltip>
                        );
                    }
                    return button;
                }
                return (
                    <Button variant="soft" size="sm" color="primary" onClick={() => handleGift(params.row.id, true)}>
                        지급 완료
                    </Button>
                );
            }
        },
        { field: "memo", headerName: "메모", width: 260, flex: 1, editable: true },
    ];

    return (
        <div style={{ width: "96%" }}>
            <div style={{
                fontFamily: "Apple_B",
                marginTop: "50px",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}>
                <div>
                    네이버 리뷰 작성자
                </div>
                <Input
                    size="md"
                    placeholder="매칭된 학생 이름 또는 전화번호 검색"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ width: 300 }}
                />
            </div>

            <div className={classes.root} style={{ height: "650px", width: "100%", marginTop: "24px" }}>
                <DataGridPremium
                    rows={filteredRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    disableRowSelectionOnClick
                    processRowUpdate={handleEditCommit}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                />
            </div>

            <GiftCashCard refreshKey={cashRefreshKey} />

            <ImageModal2 open={imageOpen} handleClose={() => setImageOpen(false)} src={[imageSrc]} isMultiple={false} />

            {/* 지급하기 — 매칭 0명/여러 명일 때 차감 지점 선택 */}
            <Modal open={!!branchPickRow} onClose={() => setBranchPickRow(null)}>
                <ModalDialog sx={{ width: 360 }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>차감할 지점 선택</div>
                    {branchPickRow &&
                        <div style={{ fontSize: 13, color: "#6B7280" }}>
                            {branchPickRow.name} ({branchPickRow.phone}) · {branchPickRow.matchedName ? `매칭 학생: ${branchPickRow.matchedName} (${branchPickRow.matchedLocation})` : "매칭된 학생 없음"}
                        </div>
                    }
                    <RadioGroup value={pickedBranch} onChange={(e) => setPickedBranch(e.target.value)} sx={{ gap: 1, mt: 1 }}>
                        {GIFT_CASH_BRANCHES.map((b) => (
                            <Radio key={b.key} value={b.key} label={b.sub ? `${b.label} (${b.sub})` : b.label} size="sm" />
                        ))}
                    </RadioGroup>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                        <Button variant="plain" color="neutral" onClick={() => setBranchPickRow(null)}>취소</Button>
                        <Button disabled={!pickedBranch || !!sendingId} loading={!!sendingId} onClick={() => sendGift(branchPickRow, pickedBranch)}>
                            지급하기
                        </Button>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default NaverReviewWriter;
